// ============================================================================
// Our own tracking view for one shipment — the same story LogiNext's page tells,
// built from the trail we already hold.
//
// It exists because the embedded LogiNext page carries THEIR session: an operator
// not signed in to Loginext in this browser, or on a browser that blocks the
// third-party cookies it needs (Safari, by default), gets their login screen
// instead of the shipment. That is a coin toss on a page the desk needs to work.
//
// WHAT THIS CANNOT SHOW is the live vehicle position — LogiNext has the GPS feed
// and we do not store it. So there is no moving dot here, and the panel says so
// rather than letting a stale pin read as a current one.
// ============================================================================
import { EnvironmentOutlined } from "@ant-design/icons";
import { Descriptions, Empty, Spin, Steps, Tag, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { fmtIST } from "../format";
import { getShipmentTracking, ShipmentTracking } from "./carrier.api";

const STATUS_COLOR: Record<string, string> = {
  delivered: "green",
  in_transit: "blue",
  attempted: "orange",
  booked: "cyan",
  created: "default",
  cancelled: "red",
};

// Ant's Step status, from the severity the server puts on each event.
const STEP_STATUS: Record<string, "finish" | "error" | "process"> = {
  success: "finish",
  warning: "error",
  error: "error",
  info: "process",
};

const mapsLink = (lat: number | null, lng: number | null) =>
  lat && lng ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}` : null;

const Place: React.FC<{ end: ShipmentTracking["origin"] }> = ({ end }) => {
  const link = mapsLink(end.lat, end.lng);
  return (
    <>
      {end.label || <span style={{ color: "#bbb" }}>—</span>}
      {end.address && (
        <div style={{ color: "#8c8c8c", fontSize: 12 }}>
          {end.address}
          {link && (
            <a href={link} target="_blank" rel="noreferrer" style={{ marginLeft: 6 }}>
              <EnvironmentOutlined />
            </a>
          )}
        </div>
      )}
    </>
  );
};

const TrackingPanel: React.FC<{ docId: number }> = ({ docId }) => {
  const [data, setData] = useState<ShipmentTracking | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getShipmentTracking(docId).then((res) => {
      if (cancelled) return;
      setData(res);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [docId]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }
  if (!data) return <Empty description="Could not load tracking for this shipment" />;

  return (
    <div style={{ padding: 4 }}>
      <Descriptions column={2} size="small" bordered style={{ marginBottom: 20 }}>
        <Descriptions.Item label="Status">
          <Tag color={STATUS_COLOR[data.delivery_status] ?? "default"}>
            {data.delivery_status.replace(/_/g, " ")}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Trip">
          {data.trip_name || <span style={{ color: "#bbb" }}>—</span>}
        </Descriptions.Item>
        <Descriptions.Item label="From">
          <Place end={data.origin} />
        </Descriptions.Item>
        <Descriptions.Item label="To">
          <Place end={data.destination} />
        </Descriptions.Item>
        <Descriptions.Item label="Vehicle">
          {data.vehicle_number || <span style={{ color: "#bbb" }}>Not set</span>}
        </Descriptions.Item>
        <Descriptions.Item label="Driver">
          {data.driver_name || <span style={{ color: "#bbb" }}>Not set</span>}
          {data.driver_number ? <span style={{ color: "#888" }}> · {data.driver_number}</span> : null}
        </Descriptions.Item>
        <Descriptions.Item label={data.delivered_at ? "Delivered" : "ETA"}>
          {data.delivered_at
            ? fmtIST(data.delivered_at, "D MMM, h:mm A")
            : data.eta
            ? fmtIST(data.eta, "D MMM, h:mm A")
            : <span style={{ color: "#bbb" }}>Not known</span>}
        </Descriptions.Item>
        <Descriptions.Item label="Carrier ref">
          {data.provider_ref || <span style={{ color: "#bbb" }}>—</span>}
        </Descriptions.Item>
      </Descriptions>

      <Typography.Text strong>Progress</Typography.Text>
      {data.events.length === 0 ? (
        <Empty
          style={{ marginTop: 12 }}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="The carrier has not reported anything on this shipment yet"
        />
      ) : (
        <Steps
          direction="vertical"
          size="small"
          style={{ marginTop: 16 }}
          current={data.events.length - 1}
          items={data.events.map((e) => ({
            title: e.label,
            description: fmtIST(e.occurred_at, "ddd, D MMM · h:mm A"),
            status: STEP_STATUS[e.severity] ?? "process",
          }))}
        />
      )}

      {/* Said plainly: this view is complete except for the one thing only LogiNext has. */}
      <div style={{ marginTop: 20, color: "#8c8c8c", fontSize: 12 }}>
        Built from our own record of this shipment. The live vehicle position is LogiNext's — use
        their view for a moving map.
      </div>
    </div>
  );
};

export default TrackingPanel;
